import type { SourcePostFilters } from '@/lib/serpRead/types';
import { serializeArea } from '@/lib/areaSearch';
import { SERP_PAGE_SIZE } from '@/lib/serpRead/serpQuery';

/**
 * Pure SERP query helpers — no supabase import, so the filter/sort/pagination
 * logic is unit-testable with a recording fake (see serpQuery.test.ts).
 *
 * ŚCIEŻKA ODCZYTU (wybór ścieżki, kolumny, łańcuch filtrów, sortowanie, sekcje) mieszka od F3
 * w `src/lib/serpRead/` — module wspólnym z funkcją `send-filter-alerts` (plan §1.68). Ten plik
 * eksportuje ją dalej, żeby dotychczasowe importy `@/hooks/serpQuery` działały bez zmian, i
 * trzyma to, co jest TYLKO aplikacji: klucz React Query, etykiety miejsca, stronicowanie.
 */
export {
  AREA_SANITY_MAX,
  AREA_SANITY_MIN,
  KOLUMNY_RANKINGU,
  KOLUMNY_SORTOWANIA_PINOW,
  PINS_LIMIT,
  PIN_COLUMNS,
  RENT_SANITY_MAX,
  SERP_COLUMNS,
  SERP_GRACE_MS,
  SERP_PAGE_SIZE,
  applyPinSort,
  applySekcja,
  applySerpFilters,
  applySerpSort,
  buildAreasOrClause,
  buildRoomsClause,
  costColumn,
  dzisiejszaDataLokalna,
  hasPolygon,
  kolumnyPinow,
  sciezkaOdczytu,
  type SciezkaOdczytu,
  type SekcjaListy,
  type SerpFilterableQuery,
} from '@/lib/serpRead/serpQuery';

/**
 * Normalized queryKey fragment: drops `view` (rendering mode, not a data
 * filter — keeping it would refetch on list↔map toggle), omits
 * undefined/inactive fields, sorts array filters so order never busts the
 * cache.
 */
export function serpFilterKey(filters: SourcePostFilters): Record<string, unknown> {
  const key: Record<string, unknown> = {};
  if (filters.postingTypes?.length) key.postingTypes = [...filters.postingTypes].sort();
  if (filters.suburbs?.length) key.suburbs = [...filters.suburbs].sort();
  if (filters.areas?.length) key.areas = filters.areas.map(serializeArea).sort();
  if (typeof filters.rentMin === 'number') key.rentMin = filters.rentMin;
  if (typeof filters.rentMax === 'number') key.rentMax = filters.rentMax;
  if (filters.costMode === 'total') key.costMode = 'total';
  if (filters.zeZwierzeciem) key.zeZwierzeciem = true;
  if (filters.typBudynku) key.typBudynku = filters.typBudynku;
  if (filters.wiekBudynku) key.wiekBudynku = filters.wiekBudynku;
  if (filters.umeblowanie) key.umeblowanie = filters.umeblowanie;
  if (filters.pietro) key.pietro = filters.pietro;
  if (filters.kuchnia) key.kuchnia = filters.kuchnia;
  if (filters.lazienki) key.lazienki = filters.lazienki;
  if (filters.balkon) key.balkon = true;
  if (filters.winda) key.winda = true;
  if (filters.kaucjaMax != null) key.kaucjaMax = filters.kaucjaMax;
  if (filters.udogodnienia?.length) key.udogodnienia = [...filters.udogodnienia].sort();
  if (filters.internet) key.internet = filters.internet;
  // Podfiltr garażu zmienia tylko kolejność (offers_ranked), ale to też nowe zapytanie: bez klucza
  // przełączenie zostawiało listę z pamięci podręcznej w starej kolejności.
  if (filters.garaz) key.garaz = true;
  if (filters.nieznaneOplaty) key.nieznaneOplaty = true;
  if (filters.bezZdjec === false) key.bezZdjec = false;
  if (filters.bezAdresu) key.bezAdresu = true;
  if (filters.rooms?.length) key.rooms = [...filters.rooms].sort();
  if (typeof filters.areaMin === 'number') key.areaMin = filters.areaMin;
  if (typeof filters.areaMax === 'number') key.areaMax = filters.areaMax;
  if (filters.nearMetro) key.nearMetro = true;
  if (filters.poi?.length) key.poi = [...filters.poi].sort();
  if (filters.postedSinceHours) key.postedSinceHours = filters.postedSinceHours;
  if (filters.includeDead) key.includeDead = true;
  if (filters.noPrice) key.noPrice = true;
  if (filters.approxLocation) key.approxLocation = true;
  if (filters.sanityOutliers) key.sanityOutliers = true;
  if (filters.availableNow) key.availableNow = true;
  if (filters.wlascicielNaFlatHero) key.wlascicielNaFlatHero = true;
  // Lista w kluczu: odklikane serduszko przy włączonym filtrze zmienia wynik.
  if (filters.ulubione) key.ulubione = [...(filters.ulubioneIds ?? [])].sort();
  if (filters.sort && filters.sort !== 'newest') key.sort = filters.sort;
  if (filters.polygon && filters.polygon.length >= 3) key.polygon = filters.polygon;
  return key;
}

/**
 * Location label for cards / map popups / the offer headline. Warsaw posts
 * read "suburb · subdistrict"; non-Warsaw posts lead with the CITY, so even a
 * leftover LLM Warsaw-vocabulary suburb can't masquerade as a Warsaw district.
 * Skips empty parts, so a missing suburb never renders as "null · X".
 */
export function serpLocationLabel(
  city: string | null | undefined,
  suburb: string | null | undefined,
  subdistrict: string | null | undefined,
): string | null {
  const parts =
    city && city !== 'Warszawa'
      ? [city, suburb ?? subdistrict]
      : [suburb, subdistrict];
  // Część równa poprzedniej (bez względu na wielkość liter i odstępy) — raz. „Wawer · Wawer”:
  // MSI ma rejon „Wawer” w dzielnicy Wawer, dane są poprawne, powtórzenie było tylko w napisie
  // (zgłoszenie Piotra 29.09, 0cf61e24 i 2c5799e2). To samo dla miasta poza Warszawą.
  const klucz = (x: string) => x.trim().toLocaleLowerCase('pl').replace(/\s+/g, ' ');
  const bezPowtorzen: string[] = [];
  for (const p of parts) {
    if (!p || !p.trim()) continue;
    if (bezPowtorzen.some((q) => klucz(q) === klucz(p))) continue;
    bezPowtorzen.push(p);
  }
  const label = bezPowtorzen.join(' · ');
  return label || null;
}

interface PageLike {
  rows: { id: string }[];
  count?: number | null;
}

/**
 * Next page index for useInfiniteQuery. A short page means the end; with an
 * exact count we also stop when all rows are fetched — otherwise a total
 * that's an exact multiple of the page size would issue one extra request
 * for an out-of-range Range (PostgREST rejects it on some versions).
 */
export function getNextSerpPageParam(lastPage: PageLike, allPages: PageLike[]): number | undefined {
  if (lastPage.rows.length < SERP_PAGE_SIZE) return undefined;
  const fetched = allPages.reduce((n, p) => n + p.rows.length, 0);
  if (lastPage.count != null && fetched >= lastPage.count) return undefined;
  return allPages.length;
}

/**
 * Pages → flat list, deduped by id (first occurrence wins). Insurance against
 * offset drift: a post inserted mid-scroll under sort=newest shifts offsets
 * and can re-serve a row on the next page.
 */
export function flattenSerpPages<T extends { id: string }>(pages?: { rows: T[] }[]): T[] {
  if (!pages?.length) return [];
  const seen = new Set<string>();
  const out: T[] = [];
  for (const page of pages) {
    for (const row of page.rows) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      out.push(row);
    }
  }
  return out;
}

/**
 * Najwęższa nazwa miejsca, jaką znamy — do karty nad mapą.
 *
 * Karta w wynikach mówi „Śródmieście · Muranów", bo tam liczy się kontekst: lista miesza
 * dzielnice i trzeba wiedzieć, gdzie to jest. Nad mapą kontekst już widać (mapa JEST
 * kontekstem), a miejsca jest 136 px na całą kartę — „Śródmieście ·…" ucinało osiedle, czyli
 * jedyną nową informację. Bierzemy więc samo osiedle, a dzielnicę dopiero, gdy osiedla nie ma.
 */
export function serpNajwezszaNazwa(
  city: string | null | undefined,
  suburb: string | null | undefined,
  subdistrict: string | null | undefined,
): string | null {
  return subdistrict || suburb || (city && city !== 'Warszawa' ? city : null) || null;
}

